import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getApiBaseUrl } from "../../config";

const baseQuery = fetchBaseQuery({
  baseUrl: getApiBaseUrl(),
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
            const user = getState().user?.currentUser;
            const token = getState().companyProfile?.token;
            if (token) {
                headers.set("x-auth-token", token);
            } else if (user?.id) {
                headers.set("userid", user.id);
            }
            return headers;
        },
});

export const reportsApi = createApi({
  reducerPath: "reportsApi",
  baseQuery,
  tagTypes: ["Reports", "Analytics", "MembershipStats"],
  endpoints: (builder) => ({
    // Get membership analytics data
    getMembershipAnalytics: builder.query({
      async queryFn(params = {}, queryApi, extraOptions, baseQuery) {
        const { startDate, endDate, status, membershipType, registrationType } =
          params;

        const queryParams = new URLSearchParams();
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);
        if (status) queryParams.append("status", status);
        if (membershipType)
          queryParams.append("membershipType", membershipType);
        if (registrationType)
          queryParams.append("registrationType", registrationType);

        const result = await baseQuery({
          url: `reports/membership-analytics?${queryParams.toString()}`,
        });

        return result;
      },
      providesTags: ["Analytics", "MembershipStats"],
    }),

    // Get registration trends data
    getRegistrationTrends: builder.query({
      async queryFn(params = {}, queryApi, extraOptions, baseQuery) {
        const { period = "12months", groupBy = "month" } = params;

        const result = await baseQuery({
          url: `reports/registration-trends?period=${period}&groupBy=${groupBy}`,
        });

        return result;
      },
      providesTags: ["Analytics"],
    }),

    // Get membership status distribution
    getStatusDistribution: builder.query({
      async queryFn(params = {}, queryApi, extraOptions, baseQuery) {
        const { year, quarter } = params;

        const queryParams = new URLSearchParams();
        if (year) queryParams.append("year", year);
        if (quarter) queryParams.append("quarter", quarter);

        const result = await baseQuery({
          url: `reports/status-distribution?${queryParams.toString()}`,
        });

        return result;
      },
      providesTags: ["MembershipStats"],
    }),

    // Get membership tier analytics
    getTierAnalytics: builder.query({
      async queryFn(params = {}, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: "reports/tier-analytics",
        });

        return result;
      },
      providesTags: ["MembershipStats"],
    }),

    // Get financial summary
    getFinancialSummary: builder.query({
      async queryFn(params = {}, queryApi, extraOptions, baseQuery) {
        const { startDate, endDate, groupBy = "month" } = params;

        const queryParams = new URLSearchParams();
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);
        queryParams.append("groupBy", groupBy);

        const result = await baseQuery({
          url: `reports/financial-summary?${queryParams.toString()}`,
        });

        return result;
      },
      providesTags: ["Analytics"],
    }),

    // Get member activity report
    getMemberActivity: builder.query({
      async queryFn(params = {}, queryApi, extraOptions, baseQuery) {
        const { memberId, startDate, endDate } = params;

        const queryParams = new URLSearchParams();
        if (memberId) queryParams.append("memberId", memberId);
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);

        const result = await baseQuery({
          url: `reports/member-activity?${queryParams.toString()}`,
        });

        return result;
      },
      providesTags: ["Reports"],
    }),

    // Export report data
    exportReportData: builder.mutation({
      async queryFn(params, queryApi, extraOptions, baseQuery) {
        const { reportType, format, filters = {} } = params;

        const result = await baseQuery({
          url: "reports/export",
          method: "POST",
          body: {
            reportType,
            format,
            filters,
            timestamp: new Date().toISOString(),
          },
        });

        return result;
      },
    }),

    // Generate custom report
    generateCustomReport: builder.mutation({
      async queryFn(params, queryApi, extraOptions, baseQuery) {
        const {
          reportName,
          dataSource,
          filters,
          groupBy,
          aggregations,
          dateRange,
        } = params;

        const result = await baseQuery({
          url: "reports/custom",
          method: "POST",
          body: {
            reportName,
            dataSource,
            filters,
            groupBy,
            aggregations,
            dateRange,
            createdAt: new Date().toISOString(),
          },
        });

        return result;
      },
      invalidatesTags: ["Reports"],
    }),

    // Get report templates
    getReportTemplates: builder.query({
      async queryFn(args, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: "reports/templates",
        });

        return result;
      },
      providesTags: ["Reports"],
    }),

    // Save report template
    saveReportTemplate: builder.mutation({
      async queryFn(params, queryApi, extraOptions, baseQuery) {
        const { templateName, configuration, description } = params;

        const result = await baseQuery({
          url: "reports/templates",
          method: "POST",
          body: {
            templateName,
            configuration,
            description,
            createdAt: new Date().toISOString(),
          },
        });

        return result;
      },
      invalidatesTags: ["Reports"],
    }),

    // Get dashboard summary
    getDashboardSummary: builder.query({
      async queryFn(args, queryApi, extraOptions, baseQuery) {
        const queryParams = new URLSearchParams();
        if (args?.preset) queryParams.append("preset", args.preset);
        if (args?.startDate) queryParams.append("startDate", args.startDate);
        if (args?.endDate) queryParams.append("endDate", args.endDate);
        if (args?.month) queryParams.append("month", args.month);
        if (args?.year) queryParams.append("year", args.year);
        const suffix = queryParams.toString()
          ? `?${queryParams.toString()}`
          : "";
        const result = await baseQuery({
          url: `reports/dashboard-summary${suffix}`,
        });

        return result;
      },
      providesTags: ["Analytics", "MembershipStats"],
    }),

    // Get Risk & Compliance Red/Orange/Green counts
    getComplianceSummary: builder.query({
      async queryFn(args, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: "reports/compliance-summary",
        });

        return result;
      },
      providesTags: ["Analytics", "MembershipStats"],
    }),

    getRegistrationSummary: builder.query({
      async queryFn(args, queryApi, extraOptions, baseQuery) {
        const queryParams = new URLSearchParams();
        if (args?.year) queryParams.append("year", String(args.year));
        const suffix = queryParams.toString()
          ? `?${queryParams.toString()}`
          : "";
        const result = await baseQuery({
          url: `reports/registration-summary${suffix}`,
        });
        return result;
      },
      providesTags: ["Analytics", "MembershipStats"],
    }),

    getRenewalSummary: builder.query({
      async queryFn(args, queryApi, extraOptions, baseQuery) {
        const queryParams = new URLSearchParams();
        if (args?.year) queryParams.append("year", String(args.year));
        const suffix = queryParams.toString()
          ? `?${queryParams.toString()}`
          : "";
        const result = await baseQuery({
          url: `reports/renewal-summary${suffix}`,
        });
        return result;
      },
      providesTags: ["Analytics", "MembershipStats"],
    }),

    getMvdSummary: builder.query({
      async queryFn(args, queryApi, extraOptions, baseQuery) {
        const queryParams = new URLSearchParams();
        if (args?.year) queryParams.append("year", String(args.year));
        if (args?.month) queryParams.append("month", String(args.month));
        const suffix = queryParams.toString()
          ? `?${queryParams.toString()}`
          : "";
        const result = await baseQuery({
          url: `reports/mvd-summary${suffix}`,
        });
        return result;
      },
      providesTags: ["Analytics", "MembershipStats"],
    }),

    getPaymentSummary: builder.query({
      async queryFn(args, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: "reports/payment-summary",
        });
        return result;
      },
      providesTags: ["Analytics", "MembershipStats"],
    }),
  }),
});

export const {
  useGetMembershipAnalyticsQuery,
  useGetRegistrationTrendsQuery,
  useGetStatusDistributionQuery,
  useGetTierAnalyticsQuery,
  useGetFinancialSummaryQuery,
  useGetMemberActivityQuery,
  useExportReportDataMutation,
  useGenerateCustomReportMutation,
  useGetReportTemplatesQuery,
  useSaveReportTemplateMutation,
  useGetDashboardSummaryQuery,
  useGetComplianceSummaryQuery,
  useGetRegistrationSummaryQuery,
  useGetRenewalSummaryQuery,
  useGetMvdSummaryQuery,
  useGetPaymentSummaryQuery,
} = reportsApi;
